import { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../../store/store';
import { API_ENDPOINTS } from '../../../api/endpoints';
import { cancelRequest } from '../../../api/api';
import UsageProgressBar from '../../../components/common/UsageProgressBar';
import SubscriptionCompanyForm from './SubscriptionCompanyForm';
import { formatPeriodDate } from '../../../utils/contractPeriods';

const SubscriptionCompanies = () => {
  const store = useStore();
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isAddingCompany, setIsAddingCompany] = useState(false);

  const loadCompanies = useCallback(() => {
    setIsLoading(true);
    setError(null);

    store.makeRequest({
      endpoint: API_ENDPOINTS.SUBSCRIPTION_LIST_COMPANIES,
      onSuccess: (data) => {
        setCompanies(data || []);
      },
      onError: (errorMessage) => {
        setError(errorMessage || 'Failed to load subscription companies');
      },
      onFinally: () => {
        setIsLoading(false);
      }
    });
  }, [store]);

  useEffect(() => {
    loadCompanies();
    return () => {
      cancelRequest(API_ENDPOINTS.SUBSCRIPTION_LIST_COMPANIES);
    };
  }, [loadCompanies]);

  const handleAddCompany = () => {
    setSelectedCompanyId(null);
    setIsAddingCompany(true);
  };

  const handleSelectCompany = (companyId) => {
    setIsAddingCompany(false);
    setSelectedCompanyId(companyId);
  };

  const handleFormClose = () => {
    setSelectedCompanyId(null);
    setIsAddingCompany(false);
  };

  const handleFormSave = () => {
    handleFormClose();
    loadCompanies();
  };

  const showForm = isAddingCompany || selectedCompanyId !== null;

  if (isLoading) {
    return (
      <div className="subscription-companies">
        <div className="subscription-companies-header">
          <h2>Subscription Companies</h2>
        </div>
        <div className="subscription-companies-loading">Loading companies...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-companies">
        <div className="subscription-companies-header">
          <h2>Subscription Companies</h2>
        </div>
        <div className="subscription-companies-error" role="alert">
          {error}
          <button className="subscription-btn-retry" onClick={loadCompanies} type="button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-companies">
      <div className="subscription-companies-header">
        <h2>Subscription Companies</h2>
        <button className="subscription-btn-add-company" onClick={handleAddCompany} type="button">
          Add Company
        </button>
      </div>

      {companies.length === 0 ? (
        <div className="subscription-companies-empty">
          No subscription companies configured yet.
        </div>
      ) : (
        <div className="subscription-companies-list">
          {companies.map((company) => {
            const hasDownloadLimit = company.profiler_download_limit != null;
            const hasTokenLimit = company.agent_token_limit != null;

            return (
              <div
                key={company.id}
                className={`subscription-company-card ${selectedCompanyId === company.id ? 'selected' : ''}`}
                onClick={() => handleSelectCompany(company.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectCompany(company.id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Edit ${company.name}`}
              >
                <div className="subscription-company-card-header">
                  <span className="subscription-company-card-name">{company.name}</span>
                  <span className="subscription-company-card-domains">
                    {company.domains?.length === 1
                      ? '1 domain'
                      : `${company.domains?.length || 0} domains`}
                  </span>
                </div>

                <div className="subscription-company-card-usage">
                  {hasDownloadLimit ? (
                    <UsageProgressBar
                      label="Downloads"
                      used={company.download_count ?? 0}
                      limit={company.profiler_download_limit}
                    />
                  ) : (
                    <div className="subscription-company-card-unlimited">Downloads: Unlimited</div>
                  )}
                </div>

                <div className="subscription-company-card-usage">
                  {hasTokenLimit ? (
                    <UsageProgressBar
                      label="Tokens"
                      used={company.token_usage ?? 0}
                      limit={company.agent_token_limit}
                      unit="tokens"
                    />
                  ) : (
                    <div className="subscription-company-card-unlimited">Tokens: Unlimited</div>
                  )}
                </div>

                <div className="subscription-company-card-usage">
                  <UsageProgressBar
                    label="Contact credits"
                    used={company.enrichment_usage ?? 0}
                    limit={company.enrichment_credit_limit ?? 0}
                    unit="credits"
                  />
                </div>

                {company.subscription_expiry && (
                  <div className="subscription-company-card-meta">
                    Expires: {formatPeriodDate(company.subscription_expiry)}
                  </div>
                )}

                {company.notes && (
                  <div className="subscription-company-card-meta">{company.notes}</div>
                )}

                {hasDownloadLimit && (
                  <button
                    className="subscription-btn-increase-limit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectCompany(company.id);
                    }}
                    type="button"
                  >
                    Increase limit
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <SubscriptionCompanyForm
          companyId={isAddingCompany ? null : selectedCompanyId}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
};

export default SubscriptionCompanies;
